import React, { useEffect, useRef } from 'react';

// ── DreamyAmbience ────────────────────────────────────────────────────────────
// A single fixed <canvas> that floats a gentle layer of drifting "stardust" and
// the occasional shooting star over the whole app — the signature dreamy feel,
// present on every page without touching any page. It sits ABOVE page content
// but BELOW navbars/dropdowns/modals (z-index 2) and is click-through
// (pointer-events:none), so it never interferes with the UI.
//
// Built for every screen + battery:
//   • particle count scales with viewport (≈36 on phones, ≤95 on desktop)
//   • devicePixelRatio-aware, capped at 2 for retina sharpness without waste
//   • pauses entirely when the tab is hidden
//   • recolours instantly when the theme flips (light ↔ dark)
//   • honours prefers-reduced-motion: renders one calm static frame, no motion
//
// The per-element page entrance is handled separately by pure CSS (dreamy.css),
// scoped to the .dream-page wrapper — not here.
const DreamyAmbience = () => {
  const canvasRef = useRef(null);

  // ── Ambient canvas ──────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
    let prefersReduced = reduceMQ.matches;

    let w = 0, h = 0, dpr = 1;
    let particles = [];
    let shooting = null;
    let raf = 0;
    let running = true;
    let theme = document.documentElement.getAttribute('data-theme') || 'light';

    const rand = (a, b) => a + Math.random() * (b - a);

    // Soft palettes: bright on night, muted pastel "dust" on the light theme.
    const palette = () => (theme === 'dark'
      ? ['255,255,255', '201,168,255', '253,224,116']  // white · lilac · gold
      : ['111,66,193', '155,111,232', '240,165,192']); // purple · violet · pink

    const makeParticles = () => {
      const colors = palette();
      let count = Math.round((w * h) / 16000);
      count = Math.max(14, Math.min(count, w < 600 ? 36 : 95));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: rand(0.7, 2.8),
        baseA: rand(0.12, 0.5),
        a: 0,
        tw: rand(0.005, 0.02),
        twp: Math.random() * Math.PI * 2,
        vx: rand(-0.06, 0.06),
        vy: rand(-0.14, -0.03), // drift gently upward, like embers
        c: colors[Math.floor(Math.random() * colors.length)],
      }));
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      makeParticles();
      if (prefersReduced) drawStatic();
    };

    const drawParticle = (p) => {
      const rad = p.r * 4;
      const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rad);
      g.addColorStop(0, `rgba(${p.c},${p.a})`);
      g.addColorStop(1, `rgba(${p.c},0)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(p.x, p.y, rad, 0, Math.PI * 2);
      ctx.fill();
    };

    const maybeShoot = () => {
      if (shooting || Math.random() > 0.0035) return; // rare + magical
      const fromLeft = Math.random() > 0.5;
      shooting = {
        x: fromLeft ? -60 : w + 60,
        y: rand(0, h * 0.45),
        vx: (fromLeft ? 1 : -1) * rand(6, 10),
        vy: rand(2, 4),
        life: 1,
      };
    };

    const drawShoot = () => {
      if (!shooting) return;
      const s = shooting;
      const c = palette()[0];
      ctx.strokeStyle = `rgba(${c},${0.5 * s.life})`;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(s.x - s.vx * 6, s.y - s.vy * 6);
      ctx.stroke();
      s.x += s.vx;
      s.y += s.vy;
      s.life -= 0.012;
      if (s.life <= 0 || s.x < -120 || s.x > w + 120) shooting = null;
    };

    const frame = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        p.twp += p.tw;
        p.a = p.baseA * (0.45 + 0.55 * Math.sin(p.twp));
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -12) { p.y = h + 12; p.x = Math.random() * w; }
        if (p.x < -12) p.x = w + 12;
        else if (p.x > w + 12) p.x = -12;
        drawParticle(p);
      }
      maybeShoot();
      drawShoot();
      if (running) raf = requestAnimationFrame(frame);
    };

    function drawStatic() {
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) { p.a = p.baseA; drawParticle(p); }
    }

    const start = () => {
      cancelAnimationFrame(raf);
      if (prefersReduced) { drawStatic(); return; }
      running = true;
      raf = requestAnimationFrame(frame);
    };

    resize();
    start();

    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    const onVis = () => {
      if (document.hidden) { running = false; cancelAnimationFrame(raf); }
      else start();
    };
    document.addEventListener('visibilitychange', onVis);

    const onReduceChange = () => { prefersReduced = reduceMQ.matches; start(); };
    if (reduceMQ.addEventListener) reduceMQ.addEventListener('change', onReduceChange);

    const themeObserver = new MutationObserver(() => {
      const t = document.documentElement.getAttribute('data-theme') || 'light';
      if (t !== theme) { theme = t; makeParticles(); if (prefersReduced) drawStatic(); }
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
      if (reduceMQ.removeEventListener) reduceMQ.removeEventListener('change', onReduceChange);
      themeObserver.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2 }}
    />
  );
};

export default DreamyAmbience;
